import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { FormField } from '~/components/system/form-field';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useAttributeCategoryMutations } from '~/hooks/use-attribute-categories';
import { attributeCategorySchema, EMPTY_ATTRIBUTE_CATEGORY, type AttributeCategoryFormValues } from '~/lib/schemas/attribute-category';

interface AttributeCategoryEditDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** The category being edited, or null/undefined when adding a new one. */
	initial: AttributeCategoryFormValues | null;
}

/** Add / edit an attribute category. RHF + zod, mirroring the System CRUD edit dialogs. */
export function AttributeCategoryEditDialog({ open, onOpenChange, initial }: AttributeCategoryEditDialogProps) {
	const { t } = useTranslation();
	const { create, update } = useAttributeCategoryMutations();
	const form = useForm<AttributeCategoryFormValues>({
		resolver: zodResolver(attributeCategorySchema) as Resolver<AttributeCategoryFormValues>,
		defaultValues: initial ?? EMPTY_ATTRIBUTE_CATEGORY,
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, watch, formState } = form;
	const isEdit = Boolean(initial?.id);

	// Re-seed the form whenever the dialog opens for a different row.
	useEffect(() => {
		if (open) reset(initial ?? EMPTY_ATTRIBUTE_CATEGORY);
	}, [open, initial, reset]);

	const onSubmit = handleSubmit(async values => {
		try {
			if (values.id) await update.mutateAsync(values);
			else await create.mutateAsync(values);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	const visible = watch('iv');

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{isEdit ? t('attributes.categories.editTitle') : t('attributes.categories.addTitle')}</DialogTitle>
					<DialogDescription>{t('attributes.categories.formSubtitle')}</DialogDescription>
				</DialogHeader>

				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					<FormField label={t('attributes.categories.columns.name')} error={formState.errors.nm?.message}>
						<Input {...register('nm')} aria-invalid={Boolean(formState.errors.nm)} autoFocus />
					</FormField>
					<FormField label={t('attributes.categories.columns.sort')} error={formState.errors.si?.message}>
						<Input type="number" {...register('si', { valueAsNumber: true })} aria-invalid={Boolean(formState.errors.si)} />
					</FormField>
					<div className="flex items-center justify-between rounded-md border border-border px-4 py-3">
						<Label htmlFor="attrcat-visible">{t('attributes.categories.columns.visible')}</Label>
						<Switch id="attrcat-visible" checked={visible} onCheckedChange={next => setValue('iv', next, { shouldDirty: true })} />
					</div>

					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={create.isPending || update.isPending}>
							{create.isPending || update.isPending ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
